/*
 * Obermaier — home.js
 * Homepage behaviour. Loaded only by sections that need it:
 *   <om-hero-video>   plays the hero video when motion is allowed, pause/play toggle, pauses off-screen
 *   <om-hero-slider>  cross-fading hero slides with named progress tabs, autoplay with pause, swipe
 *   <om-tabs>         accessible tabs for the featured collection (Best of / Neu)
 *   <om-brand-index>  floating image preview that follows the pointer (desktop, hover)
 *   <om-carousel>     scroll-snap carousel with previous/next, counter and live status
 * Everything else on the homepage (reveals, scroll-driven opening) is CSS.
 */
import { define } from '@theme/core';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const pad = (n) => String(n).padStart(2, '0');

/* ---------------------------------------------------------- hero video */
class OmHeroVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.closest('.om-hero')?.querySelector('[data-video-toggle]');
    if (!this.video) return;
    this.video.muted = true;
    this.video.playsInline = true;
    this.video.removeAttribute('controls');
    this.userPaused = reducedMotion.matches;

    this.sync = this.sync.bind(this);
    this.video.addEventListener('play', this.sync);
    this.video.addEventListener('pause', this.sync);
    this.toggle?.addEventListener('click', () => {
      if (this.video.paused) {
        this.userPaused = false;
        this.play();
      } else {
        this.userPaused = true;
        this.video.pause();
      }
    });

    this.io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) this.video.pause();
      else if (!this.userPaused) this.play();
    });
    this.io.observe(this);
    this.sync();
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  play() {
    const p = this.video.play();
    if (p && typeof p.catch === 'function') p.catch(() => this.sync());
  }

  sync() {
    if (!this.toggle) return;
    const paused = this.video.paused;
    this.toggle.setAttribute('aria-pressed', String(paused));
    this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
  }
}

/* --------------------------------------------------------------- tabs */
class OmTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    if (this.tabs.length < 2) return;
    this.panels = this.tabs.map((tab) => document.getElementById(tab.getAttribute('aria-controls')));
    this.links = [...this.querySelectorAll('[data-tabs-link]')];

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => {
        const last = this.tabs.length - 1;
        let next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = i === last ? 0 : i + 1;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = i === 0 ? last : i - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = last;
        if (next === null) return;
        event.preventDefault();
        this.select(next, true);
      });
    });
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (this.panels[i]) this.panels[i].hidden = !active;
    });
    const url = this.tabs[index].dataset.url;
    if (url) this.links.forEach((link) => (link.href = url));
    if (focus) this.tabs[index].focus();
    // Components inside the newly shown panel (e.g. product cards) may need to re-measure.
    this.panels[index]?.dispatchEvent(new CustomEvent('om:panel:shown', { bubbles: true }));
  }
}

/* -------------------------------------------------------- brand index */
class OmBrandIndex extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-brand-list]');
    if (!this.list || !this.list.querySelector('.om-brands__thumb')) return;
    this.mq = matchMedia('(hover: hover) and (min-width: 990px)');
    this.frame = 0;
    this.warmed = false;

    this.onMove = (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      const { clientX, clientY } = event;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.place(clientX, clientY));
    };
    this.onEnter = () => {
      if (this.warmed) return;
      this.warmed = true;
      // Start fetching the preview images once the pointer reaches the list.
      this.list.querySelectorAll('.om-brands__thumb img').forEach((img) => (img.loading = 'eager'));
    };
    this.onFocus = (event) => {
      const link = event.target.closest('.om-brands__link');
      if (!link || !this.mq.matches) return;
      const r = link.getBoundingClientRect();
      this.place(r.left + r.width * 0.62, r.top + r.height / 2);
    };
    // The preview is only shown while its position is known (CSS: [data-tracking]). A list that
    // scrolls under a resting pointer gets :hover without a pointermove, which would otherwise
    // show the preview at the list's top-left corner.
    this.onLeave = () => this.list.removeAttribute('data-tracking');
    this.onBlur = (event) => {
      if (!this.list.contains(event.relatedTarget)) this.list.removeAttribute('data-tracking');
    };
    this.onScroll = () => {
      if (!this.list.matches(':focus-within')) this.list.removeAttribute('data-tracking');
    };

    this.list.addEventListener('pointerenter', this.onEnter);
    this.list.addEventListener('pointerover', this.onMove, { passive: true });
    this.list.addEventListener('pointermove', this.onMove, { passive: true });
    this.list.addEventListener('pointerleave', this.onLeave);
    this.list.addEventListener('focusin', this.onFocus);
    this.list.addEventListener('focusout', this.onBlur);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    window.removeEventListener('scroll', this.onScroll);
  }

  place(x, y) {
    if (!this.mq.matches) return;
    const r = this.list.getBoundingClientRect();
    const localX = x - r.left;
    this.list.style.setProperty('--x', `${Math.round(localX)}px`);
    this.list.style.setProperty('--y', `${Math.round(y - r.top)}px`);
    this.list.toggleAttribute('data-flip', localX > r.width * 0.62);
    this.list.setAttribute('data-tracking', '');
  }
}

/* ----------------------------------------------------------- carousel */
class OmCarousel extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-carousel')) return;
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    if (this.slides.length < 2) return;
    this.prev = [...this.querySelectorAll('[data-carousel-prev]')];
    this.next = [...this.querySelectorAll('[data-carousel-next]')];
    this.counts = [...this.querySelectorAll('[data-carousel-count]')];
    this.status = this.querySelector('[data-carousel-status]');
    this.index = 0;

    this.prev.forEach((b) => b.addEventListener('click', () => this.go(this.index - 1, true)));
    this.next.forEach((b) => b.addEventListener('click', () => this.go(this.index + 1, true)));

    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.update(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.io.observe(slide));
    this.update(0);

    this.onBlockSelect = (event) => {
      const i = this.slides.findIndex((slide) => slide.contains(event.target) || slide === event.target);
      if (i > -1) this.go(i);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  go(index, announce = false) {
    const target = Math.max(0, Math.min(this.slides.length - 1, index));
    const left = this.slides[target].offsetLeft - this.slides[0].offsetLeft;
    this.track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    this.update(target);
    if (announce && this.status) this.status.textContent = this.slides[target].getAttribute('aria-label') || '';
  }

  update(index) {
    if (index < 0) return;
    this.index = index;
    const last = this.slides.length - 1;
    // aria-disabled (not disabled) keeps keyboard focus on the button at either end.
    this.prev.forEach((b) => b.setAttribute('aria-disabled', String(index === 0)));
    this.next.forEach((b) => b.setAttribute('aria-disabled', String(index === last)));
    this.counts.forEach((c) => (c.textContent = `${pad(index + 1)} / ${pad(this.slides.length)}`));
  }
}

/* ---------------------------------------------------------- hero slider */
// Autoplay is driven by the CSS progress bar of the current tab: when its animation ends, the next slide
// comes. Pausing (hover, focus, off-screen, hidden tab, editor, pause button) just pauses that animation,
// so the bar always shows how long the slide still stays. No autoplay with prefers-reduced-motion.
class OmHeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.tabs = [...this.querySelectorAll('[data-slide-tab]')];
    this.toggle = this.querySelector('[data-slider-toggle]');
    this.status = this.querySelector('[data-slider-status]');
    if (this.slides.length < 2) return;

    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.reasons = new Set();
    if (reducedMotion.matches) this.removeAttribute('data-autoplay');
    this.autoplay = this.hasAttribute('data-autoplay');

    // Only the first slide's image loads with the page; the neighbours follow once the page has loaded
    // (autoplay first moves after 7 s), and all of them as soon as the visitor reaches for the navigation.
    const warmNeighbours = () => { this.warm(this.index + 1); this.warm(this.index - 1); };
    if (document.readyState === 'complete') warmNeighbours();
    else window.addEventListener('load', warmNeighbours, { once: true });
    setTimeout(warmNeighbours, 3000);
    const warmAll = () => this.slides.forEach((slide) => slide.removeAttribute('data-defer'));
    const nav = this.querySelector('.om-hero__nav');
    ['pointerenter', 'pointerdown', 'focusin'].forEach((type) => nav?.addEventListener(type, warmAll, { once: true }));

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.go(i, true));
      tab.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        this.tabs[this.index].focus();
      });
    });

    this.addEventListener('animationend', (event) => {
      if (event.animationName === 'om-slide-progress' && this.autoplay && this.reasons.size === 0) this.go(this.index + 1);
    });

    this.toggle?.addEventListener('click', () => {
      const paused = this.toggle.getAttribute('aria-pressed') !== 'true';
      this.toggle.setAttribute('aria-pressed', String(paused));
      this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      this.pause('user', paused);
    });

    // Mouse hover and keyboard focus pause; touch does not (it would never resume).
    this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && this.pause('hover', true));
    this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && this.pause('hover', false));
    this.addEventListener('focusin', () => this.pause('focus', true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.pause('focus', false);
    });

    // Swipe on touch screens.
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse') return;
      this.swipe = { x: event.clientX, y: event.clientY };
    });
    this.addEventListener('pointerup', (event) => {
      if (!this.swipe) return;
      const dx = event.clientX - this.swipe.x;
      const dy = event.clientY - this.swipe.y;
      this.swipe = null;
      if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) this.go(this.index + (dx < 0 ? 1 : -1), true);
    });

    this.io = new IntersectionObserver(([entry]) => this.pause('offscreen', !entry.isIntersecting), { threshold: 0.2 });
    this.io.observe(this);
    this.onVisibility = () => this.pause('hidden', document.hidden);
    document.addEventListener('visibilitychange', this.onVisibility);

    // Theme editor: show the selected slide and hold it.
    this.onBlockSelect = (event) => {
      const i = this.slides.indexOf(event.target);
      if (i < 0) return;
      this.go(i);
      this.pause('editor', true);
    };
    this.onBlockDeselect = (event) => {
      if (this.slides.includes(event.target)) this.pause('editor', false);
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  disconnectedCallback() {
    this.io?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  pause(reason, on) {
    if (on) this.reasons.add(reason);
    else this.reasons.delete(reason);
    this.toggleAttribute('data-paused', this.reasons.size > 0);
    // Stopped with the pause button: the current slide's bar shows full, so the active slide stays marked.
    this.toggleAttribute('data-stopped', this.reasons.has('user'));
  }

  warm(i) {
    const count = this.slides.length;
    this.slides[((i % count) + count) % count]?.removeAttribute('data-defer');
  }

  go(target, fromUser = false) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    if (next === this.index) return;
    this.warm(next);
    const from = this.slides[this.index];
    const to = this.slides[next];
    from.classList.remove('is-active');
    from.inert = true;
    to.classList.add('is-active');
    to.inert = false;
    this.tabs.forEach((tab, i) => tab.toggleAttribute('aria-current', i === next));
    this.tabs[next]?.setAttribute('aria-current', 'true');
    this.index = next;
    // Announce only what the visitor asked for; automatic changes stay silent.
    if (fromUser && this.status) {
      const title = to.querySelector('.om-hero__title')?.textContent.trim() || '';
      this.status.textContent = [to.getAttribute('aria-label'), title].filter(Boolean).join(': ');
    }
    this.warm(next + 1);
    this.warm(next - 1);
  }
}

define('om-hero-video', OmHeroVideo);
define('om-hero-slider', OmHeroSlider);
define('om-tabs', OmTabs);
define('om-brand-index', OmBrandIndex);
define('om-carousel', OmCarousel);
